import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { BarChart3, Calendar, ClipboardList, DollarSign, Megaphone, Upload, Users, MessageSquare, Send, School, FileText, GraduationCap, Download } from 'lucide-react';
import { formatCurrency } from '../../lib/constants';
import ReportCard from '../../components/ReportCard';
import { toast } from 'sonner';

export default function ParentDashboard() {
  const location = useLocation();
  const pathTab = location.pathname.split('/').pop();
  const tabMap = { academics: 'academics', attendance: 'attendance', timetable: 'timetable', teachers: 'teachers', finance: 'finance', messages: 'messages', announcements: 'announcements' };
  const [activeTab, setActiveTab] = useState(tabMap[pathTab] || 'dashboard');

  // Sync tab with URL changes from sidebar navigation
  React.useEffect(() => {
    setActiveTab(tabMap[pathTab] || 'dashboard');
  }, [pathTab]);

  const [dashboard, setDashboard] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');
  const [childTimetable, setChildTimetable] = useState([]);
  const [childAttendance, setChildAttendance] = useState(null);
  const [childResults, setChildResults] = useState([]);
  const [childTeachers, setChildTeachers] = useState([]);
  const [balances, setBalances] = useState({ opening_balance: 0, total_invoiced: 0, total_paid: 0, outstanding_balance: 0, children: [] });
  const [invoices, setInvoices] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [msgForm, setMsgForm] = useState({ recipient_id: '', recipient_role: 'admin', subject: '', content: '' });
  const [showReportCard, setShowReportCard] = useState(false);

  useEffect(() => {
    api.get('/parent/dashboard').then(r => {
      setDashboard(r.data);
      if (r.data.children?.length > 0) setSelectedChild(r.data.children[0].id);
    }).catch(() => {});
    api.get('/parent/invoices').then(r => setInvoices(r.data)).catch(() => {});
    api.get('/parent/announcements').then(r => setAnnouncements(r.data)).catch(() => {});
    api.get('/parent/balance').then(r => {
      // New response is { opening_balance, children: [...], ... }
      if (r.data && Array.isArray(r.data.children)) {
        setBalances(r.data);
      } else if (Array.isArray(r.data)) {
        // Legacy flat array
        setBalances({ opening_balance: 0, total_invoiced: 0, total_paid: 0, outstanding_balance: 0, children: r.data });
      }
    }).catch(() => {});
    api.get('/parent/messages').then(r => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedChild) {
      api.get(`/parent/child/${selectedChild}/timetable`).then(r => setChildTimetable(r.data)).catch(() => {});
      api.get(`/parent/child/${selectedChild}/attendance`).then(r => setChildAttendance(r.data)).catch(() => {});
      api.get(`/parent/child/${selectedChild}/results`).then(r => setChildResults(r.data)).catch(() => {});
      api.get(`/parent/child/${selectedChild}/teachers`).then(r => setChildTeachers(r.data)).catch(() => setChildTeachers([]));
    }
  }, [selectedChild]);

  const currentChild = dashboard?.children?.find(c => c.id === selectedChild);
  const childBalances = balances?.children || [];
  const currentBalance = childBalances.find(b => b.student_id === selectedChild);
  const accountSummary = balances || { opening_balance: 0, total_invoiced: 0, total_paid: 0, outstanding_balance: 0 };
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const groupedTT = DAYS.reduce((acc, d) => { acc[d] = childTimetable.filter(e => e.day === d).sort((a, b) => a.period - b.period); return acc; }, {});

  const uploadProof = async (invoiceId, file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/parent/invoices/${invoiceId}/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Payment proof uploaded');
      api.get('/parent/invoices').then(r => setInvoices(r.data));
      api.get('/parent/balance').then(r => setBalances(r.data));
    } catch { toast.error('Upload failed'); }
  };

  const sendMessage = async () => {
    try {
      await api.post('/parent/messages', msgForm);
      toast.success('Message sent');
      setShowMessageDialog(false); setMsgForm({ recipient_id: '', recipient_role: 'admin', subject: '', content: '' });
      api.get('/parent/messages').then(r => setMessages(r.data));
    } catch { toast.error('Failed to send'); }
  };

  const openMessageToTeacher = (teacher) => {
    setMsgForm({
      recipient_id: teacher.user_id || '',
      recipient_role: 'teacher',
      subject: '',
      content: '',
    });
    setShowMessageDialog(true);
  };

  const downloadReportPdf = async () => {
    if (!selectedChild) return;
    try {
      const response = await api.get(`/reports/report-card/${selectedChild}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_card_${currentChild?.first_name || 'student'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report card downloaded');
    } catch { toast.error('Failed to download report card'); }
  };

  return (
    <div data-testid="parent-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A]">Parent Portal</h1>
        {dashboard?.children?.length > 0 && (
          <Select value={selectedChild} onValueChange={setSelectedChild}>
            <SelectTrigger className="w-72" data-testid="parent-child-selector"><SelectValue placeholder="Select Child" /></SelectTrigger>
            <SelectContent>{dashboard.children.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} - {c.grade}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {currentChild && (
        <div className="bg-white rounded-xl border p-4 mb-6 flex flex-wrap items-center gap-4" data-testid="child-info-bar">
          <div className="w-11 h-11 rounded-full bg-[#1B2A4A] flex items-center justify-center text-white font-bold">{currentChild.first_name?.[0]}{currentChild.last_name?.[0]}</div>
          <div>
            <p className="font-bold text-[#1B2A4A]">{currentChild.first_name} {currentChild.last_name}</p>
            <div className="flex gap-2 mt-0.5"><Badge variant="outline" className="text-xs">{currentChild.admission_number}</Badge><Badge variant="secondary" className="text-xs">{currentChild.division}</Badge><Badge className="text-xs bg-[#1B2A4A] text-white">{currentChild.grade}</Badge></div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowReportCard(true)} data-testid="parent-view-report-card"><FileText className="w-3.5 h-3.5 mr-1" /> Report Card</Button>
            <Button size="sm" variant="outline" onClick={downloadReportPdf} data-testid="parent-download-report-pdf"><Download className="w-3.5 h-3.5 mr-1" /> Download PDF</Button>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard"><Users className="w-3.5 h-3.5 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="academics"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Academics</TabsTrigger>
          <TabsTrigger value="attendance"><ClipboardList className="w-3.5 h-3.5 mr-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="timetable"><Calendar className="w-3.5 h-3.5 mr-1" /> Timetable</TabsTrigger>
          <TabsTrigger value="teachers"><GraduationCap className="w-3.5 h-3.5 mr-1" /> Teachers</TabsTrigger>
          <TabsTrigger value="finance"><DollarSign className="w-3.5 h-3.5 mr-1" /> Fees</TabsTrigger>
          <TabsTrigger value="messages"><MessageSquare className="w-3.5 h-3.5 mr-1" /> Messages</TabsTrigger>
          <TabsTrigger value="announcements"><Megaphone className="w-3.5 h-3.5 mr-1" /> News</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="dashboard">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="stat-card"><p className="text-xs text-slate-500">Attendance Rate</p><p className="text-2xl font-black text-[#1B2A4A]">{childAttendance?.summary?.percentage || 100}%</p></div>
            <div className="stat-card"><p className="text-xs text-slate-500">Recent Grades</p><p className="text-2xl font-black text-emerald-600">{childResults.length}</p></div>
            <div className="stat-card"><p className="text-xs text-slate-500">Outstanding Balance</p><p className="text-2xl font-black text-amber-600">{formatCurrency(accountSummary.outstanding_balance)}</p></div>
          </div>
          {announcements.length > 0 && (
            <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4" /> Latest Announcements</CardTitle></CardHeader><CardContent>
              {announcements.slice(0, 3).map(a => (
                <div key={a.id} className="border-b border-slate-100 py-3 last:border-0">
                  <p className="font-semibold text-sm text-[#1B2A4A]">{a.title}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.content}</p>
                </div>
              ))}
            </CardContent></Card>
          )}
        </TabsContent>

        {/* Academics */}
        <TabsContent value="academics">
          {childResults.length === 0 ? <p className="text-slate-400 text-center py-8">No results yet</p> : (
            <div className="bg-white rounded-xl border shadow-sm">
              <Table>
                <TableHeader><TableRow className="data-table"><TableHead>Subject</TableHead><TableHead>Assessment</TableHead><TableHead>Marks</TableHead><TableHead>Grade</TableHead><TableHead>Comments</TableHead></TableRow></TableHeader>
                <TableBody>{childResults.map(g => (
                  <TableRow key={g.id}>
                    <TableCell className="font-semibold">{g.subject_name}</TableCell><TableCell>{g.assessment_name}</TableCell>
                    <TableCell className="font-bold">{g.marks}/{g.total_marks}</TableCell>
                    <TableCell><Badge className={g.grade_letter === 'F' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>{g.grade_letter}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">{g.comments}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance">
          {childAttendance ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <div className="stat-card text-center"><p className="text-xs text-slate-500">Total</p><p className="text-xl font-black">{childAttendance.summary.total}</p></div>
                <div className="stat-card text-center"><p className="text-xs text-slate-500">Present</p><p className="text-xl font-black text-emerald-600">{childAttendance.summary.present}</p></div>
                <div className="stat-card text-center"><p className="text-xs text-slate-500">Late</p><p className="text-xl font-black text-amber-600">{childAttendance.summary.late}</p></div>
                <div className="stat-card text-center"><p className="text-xs text-slate-500">Absent</p><p className="text-xl font-black text-red-600">{childAttendance.summary.absent}</p></div>
                <div className="stat-card text-center"><p className="text-xs text-slate-500">Rate</p><p className="text-xl font-black text-[#1B2A4A]">{childAttendance.summary.percentage}%</p></div>
              </div>
              {childAttendance.records?.length > 0 && (
                <div className="bg-white rounded-xl border shadow-sm">
                  <Table><TableHeader><TableRow className="data-table"><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                    <TableBody>{childAttendance.records.slice(0, 30).map(r => (
                      <TableRow key={r.id}><TableCell>{r.date}</TableCell><TableCell><span className={`status-${r.status} text-xs px-2 py-1 rounded-full font-semibold capitalize`}>{r.status}</span></TableCell><TableCell className="text-sm text-slate-500">{r.notes}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
              )}
            </>
          ) : <p className="text-slate-400 text-center py-8">No attendance data</p>}
        </TabsContent>

        {/* Timetable */}
        <TabsContent value="timetable">
          {DAYS.map(day => groupedTT[day]?.length > 0 && (
            <div key={day} className="mb-4">
              <h3 className="font-bold text-[#1B2A4A] mb-2 text-sm">{day}</h3>
              {groupedTT[day].map(e => (
                <div key={e.id} className="flex justify-between p-3 bg-white rounded-lg border mb-2">
                  <div><p className="font-semibold text-sm">P{e.period}: {e.subject_name}</p><p className="text-xs text-slate-500">{e.teacher_name}</p></div>
                  <div className="text-right text-xs text-slate-400"><p>{e.start_time}-{e.end_time}</p><p>{e.room}</p></div>
                </div>
              ))}
            </div>
          ))}
          {childTimetable.length === 0 && <p className="text-slate-400 text-center py-8">No timetable data</p>}
        </TabsContent>

        {/* Teachers */}
        <TabsContent value="teachers">
          {childTeachers.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No teachers assigned to your child's class yet</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teacher-contact-cards">
              {childTeachers.map(t => (
                <Card key={t.id} className="hover:shadow-md transition-shadow" data-testid={`teacher-card-${t.id}`}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                        {t.first_name?.[0]}{t.last_name?.[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-[#1B2A4A] text-sm">{t.first_name} {t.last_name}</p>
                        {t.is_class_teacher && <Badge className="bg-amber-100 text-amber-700 text-[10px] mt-0.5">Class Teacher</Badge>}
                      </div>
                    </div>
                    {t.subjects_taught?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Subjects</p>
                        <div className="flex flex-wrap gap-1">
                          {t.subjects_taught.map(s => (
                            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {t.qualification && (
                      <p className="text-xs text-slate-500 mb-3">{t.qualification}</p>
                    )}
                    <Button
                      size="sm"
                      className="w-full bg-[#1B2A4A] text-xs rounded-full"
                      onClick={() => openMessageToTeacher(t)}
                      data-testid={`message-teacher-${t.id}`}
                    >
                      <MessageSquare className="w-3.5 h-3.5 mr-1" /> Send Message
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Finance */}
        <TabsContent value="finance">
          {/* Account Summary with Opening Balance */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {accountSummary.opening_balance > 0 && (
              <div className="stat-card text-center"><p className="text-xs text-slate-500">Opening Balance</p><p className="text-xl font-black text-orange-600">{formatCurrency(accountSummary.opening_balance)}</p></div>
            )}
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Total Invoiced</p><p className="text-xl font-black text-[#1B2A4A]">{formatCurrency(accountSummary.total_invoiced)}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-slate-500">Total Paid</p><p className="text-xl font-black text-emerald-600">{formatCurrency(accountSummary.total_paid)}</p></div>
            <div className="stat-card text-center border-2 border-amber-200"><p className="text-xs text-slate-500">Outstanding</p><p className="text-xl font-black text-amber-600">{formatCurrency(accountSummary.outstanding_balance)}</p></div>
          </div>
          {invoices.length === 0 ? <p className="text-slate-400 text-center py-8">No invoices</p> : (
            <div className="bg-white rounded-xl border shadow-sm">
              <Table>
                <TableHeader><TableRow className="data-table"><TableHead>Invoice #</TableHead><TableHead>Student</TableHead><TableHead>Items</TableHead><TableHead>Amount</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                <TableBody>{invoices.filter(inv => !selectedChild || inv.student_id === selectedChild).map(inv => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono font-bold text-sm">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.student_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {inv.line_items?.length > 0 ? inv.line_items.map(li => li.description).join(', ') : inv.fee_name || 'School Fees'}
                    </TableCell>
                    <TableCell className="font-bold">{formatCurrency(inv.amount)}</TableCell><TableCell className="text-sm">{inv.due_date}</TableCell>
                    <TableCell><span className={`status-${inv.status} text-xs px-2 py-1 rounded-full font-semibold`}>{inv.status}</span></TableCell>
                    <TableCell>
                      {inv.status !== 'paid' && !inv.payment_proof && (
                        <label className="cursor-pointer"><input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => e.target.files[0] && uploadProof(inv.id, e.target.files[0])} />
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B2A4A] hover:text-[#E8A830]"><Upload className="w-3 h-3" /> Upload Proof</span>
                        </label>
                      )}
                      {inv.payment_proof && <Badge className="bg-blue-50 text-blue-700 text-xs">Proof Uploaded</Badge>}
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Messages */}
        <TabsContent value="messages">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{messages.length} messages</p>
            <Button onClick={() => { setMsgForm({ recipient_id: '', recipient_role: 'admin', subject: '', content: '' }); setShowMessageDialog(true); }} className="bg-[#1B2A4A] rounded-full text-xs font-bold h-9 px-5" data-testid="new-message-btn"><MessageSquare className="w-3.5 h-3.5 mr-1" /> New Message</Button>
          </div>
          {messages.length === 0 ? <p className="text-slate-400 text-center py-8">No messages yet. Send a message to admin or a teacher.</p> : (
            <div className="space-y-3">{messages.map(m => (
              <Card key={m.id}><CardContent className="py-3">
                <div className="flex items-start justify-between">
                  <div><p className="font-bold text-sm text-[#1B2A4A]">{m.subject || 'No subject'}</p><p className="text-xs text-slate-500">From: {m.sender_name} ({m.sender_role}) | {new Date(m.created_at).toLocaleDateString()}</p></div>
                  {!m.is_read && m.recipient_id === dashboard?.parent?.user_id && <Badge className="bg-blue-100 text-blue-700 text-[10px]">New</Badge>}
                </div>
                <p className="text-sm text-slate-600 mt-2">{m.content}</p>
              </CardContent></Card>
            ))}</div>
          )}
        </TabsContent>

        {/* Announcements */}
        <TabsContent value="announcements">
          {announcements.length === 0 ? <p className="text-slate-400 text-center py-8">No announcements</p> : (
            <div className="space-y-3">{announcements.map(a => (
              <Card key={a.id}><CardContent className="py-4">
                <p className="font-bold text-[#1B2A4A]">{a.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleDateString()} | {a.author_name || 'Admin'}</p>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{a.content}</p>
              </CardContent></Card>
            ))}</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent><DialogHeader><DialogTitle>Send Message</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">To</label>
              <Select value={msgForm.recipient_role} onValueChange={v => setMsgForm({...msgForm, recipient_role: v, recipient_id: v === 'admin' ? '' : msgForm.recipient_id})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">School Admin</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {msgForm.recipient_role === 'teacher' && childTeachers.length > 0 && (
              <div>
                <label className="text-sm font-semibold">Select Teacher</label>
                <Select value={msgForm.recipient_id} onValueChange={v => setMsgForm({...msgForm, recipient_id: v})}>
                  <SelectTrigger data-testid="message-teacher-select"><SelectValue placeholder="Choose teacher" /></SelectTrigger>
                  <SelectContent>
                    {childTeachers.map(t => (
                      <SelectItem key={t.id} value={t.user_id || t.id}>{t.first_name} {t.last_name}{t.is_class_teacher ? ' (Class Teacher)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><label className="text-sm font-semibold">Subject</label><Input value={msgForm.subject} onChange={e => setMsgForm({...msgForm, subject: e.target.value})} placeholder="Message subject" /></div>
            <div><label className="text-sm font-semibold">Message</label><Textarea rows={4} value={msgForm.content} onChange={e => setMsgForm({...msgForm, content: e.target.value})} placeholder="Type your message..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button><Button onClick={sendMessage} className="bg-[#1B2A4A]" data-testid="send-message-btn"><Send className="w-3.5 h-3.5 mr-1" /> Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Card Dialog */}
      <Dialog open={showReportCard} onOpenChange={setShowReportCard}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only"><DialogTitle>Report Card</DialogTitle></DialogHeader>
          {selectedChild && <ReportCard studentId={selectedChild} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
